<template>
  <div
    class="content-wrapper view-clinic clinic-picker"
    data-testid="dashboard-loaded"
  >
    <!-- ─── Top bar ─── -->
    <div class="topbar">
      <button class="back-btn" @click.prevent="navigateBack">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
        <span>ย้อนกลับ</span>
      </button>
      <div class="spacer"></div>
      <div v-if="userName" class="user-pill">
        <span class="avatar">{{ userInitials }}</span>
        <span class="user-name">{{ userName }}</span>
      </div>
    </div>

    <!-- ─── No workspace assigned ─── -->
    <div
      v-if="!isLoading && workspaceGroups.length === 0"
      class="no-workspace"
    >
      <div class="no-workspace-card">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="4" y="3" width="16" height="18" rx="1.5" />
          <path
            d="M9 7h.01M12 7h.01M15 7h.01M9 11h.01M12 11h.01M15 11h.01M9 15h.01M12 15h.01M15 15h.01"
          />
        </svg>
        <strong>ยังไม่มีพื้นที่ทำงาน</strong>
        <p>
          กรุณาติดต่อผู้จัดการ หรือ admin
          เพื่อเพิ่มบัญชีของท่านเข้าไปในรายชื่อ หมอ/พนักงาน ของคลินิก
        </p>
      </div>
    </div>

    <!-- ─── Picker ─── -->
    <template v-else>
      <div class="heading">
        <div class="heading-row">
          <div class="heading-text">
            <h1>เลือกพื้นที่ทำงาน</h1>
            <p>เลือกคลินิกและสาขาที่จะเข้าทำงานวันนี้</p>
          </div>
          <div class="search-wrap">
            <div class="search">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                ref="searchInput"
                v-model="query"
                type="text"
                placeholder="ค้นหาคลินิก หรือสาขา..."
              />
              <span class="kbd">{{ shortcutHint }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="picker-body">
        <!-- Pinned / favorites strip -->
        <section
          v-if="pinnedBranches.length && !query.trim()"
          class="pinned-section"
        >
          <div class="divider-label">
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linejoin="round"
              stroke-linecap="round"
            >
              <path
                d="M12 17.3l-5.5 3.2 1.5-6.2-4.8-4.2 6.3-.5L12 4l2.5 5.6 6.3.5-4.8 4.2 1.5 6.2z"
              />
            </svg>
            ปักหมุดไว้ · {{ pinnedBranches.length }}
          </div>
          <div class="pinned-grid">
            <div
              v-for="p in pinnedBranches"
              :key="p.branch.id"
              class="card pinned-card"
              @click="selectBatch(p.clinic, p.branch)"
            >
              <div class="logo-tile sm" :class="accentFor(p.clinic.code)">
                <span class="logo-initials">{{ initials(p.clinic.name) }}</span>
                <img
                  v-if="logoUrl(p.clinic)"
                  class="logo-img"
                  :src="logoUrl(p.clinic)"
                  :alt="p.clinic.name"
                  @error="onLogoError"
                />
              </div>
              <div class="pinned-text">
                <div class="pinned-name">{{ p.branch.name }}</div>
                <div class="pinned-clinic">{{ p.clinic.name }}</div>
              </div>
              <button
                class="star-btn is-fav"
                aria-label="เลิกปักหมุด"
                @click.stop="toggleFav(p.branch.id)"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linejoin="round"
                  stroke-linecap="round"
                >
                  <path
                    d="M12 17.3l-5.5 3.2 1.5-6.2-4.8-4.2 6.3-.5L12 4l2.5 5.6 6.3.5-4.8 4.2 1.5 6.2z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </section>

        <!-- Empty search result -->
        <div v-if="filteredGroups.length === 0" class="empty">
          <strong>ไม่พบผลลัพธ์ที่ตรงกับ “{{ query }}”</strong>
          ลองค้นด้วยชื่อคลินิก หรือชื่อสาขา
        </div>

        <template v-else>
          <div
            v-if="pinnedBranches.length && !query.trim()"
            class="divider-label"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="4" y="3" width="16" height="18" rx="1.5" />
              <path
                d="M9 7h.01M12 7h.01M15 7h.01M9 11h.01M12 11h.01M15 11h.01M9 15h.01M12 15h.01M15 15h.01"
              />
            </svg>
            คลินิกทั้งหมด · {{ filteredGroups.length }}
          </div>

          <div class="clinic-grid">
            <article
              v-for="g in filteredGroups"
              :key="g.code"
              class="card clinic-card"
            >
              <header class="clinic-head">
                <div class="logo-tile lg" :class="accentFor(g.code)">
                  <span class="logo-initials">{{ initials(g.name) }}</span>
                  <img
                    v-if="logoUrl(g)"
                    class="logo-img"
                    :src="logoUrl(g)"
                    :alt="g.name"
                    @error="onLogoError"
                  />
                </div>
                <div class="clinic-head-text">
                  <div class="clinic-name">{{ g.name }}</div>
                  <div class="clinic-chips">
                    <span class="chip">{{ g.branches.length }} สาขา</span>
                    <template v-if="g.isCentral">
                      <span
                        v-for="(role, i) in centralRoles(g)"
                        :key="role.key"
                        class="chip accent"
                      >
                        <svg
                          v-if="i === 0"
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.8"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M12 3l8 3v6c0 4.5-3.5 8.3-8 9-4.5-.7-8-4.5-8-9V6l8-3z" />
                        </svg>
                        {{ role.label }}
                      </span>
                    </template>
                  </div>
                </div>
              </header>

              <div class="branch-list">
                <!-- Central / clinic-level management row -->
                <div
                  v-if="g.isCentral"
                  class="branch-btn is-manage"
                  role="button"
                  tabindex="0"
                  @click="enterCentral(g)"
                  @keydown.enter="enterCentral(g)"
                >
                  <svg
                    class="branch-glyph"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M12 3l8 3v6c0 4.5-3.5 8.3-8 9-4.5-.7-8-4.5-8-9V6l8-3z" />
                  </svg>
                  <div class="branch-main">
                    <span class="branch-name">จัดการระดับคลินิก</span>
                  </div>
                  <span class="branch-arrow">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </span>
                </div>

                <!-- Branch rows -->
                <div
                  v-for="b in g.branches"
                  :key="b.id"
                  class="branch-btn"
                  role="button"
                  tabindex="0"
                  @click="selectBatch(g, b)"
                  @keydown.enter="selectBatch(g, b)"
                >
                  <svg
                    class="branch-glyph"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M3 9l1.5-5h15L21 9" />
                    <path d="M3 9v11h18V9" />
                    <path d="M9 20v-6h6v6" />
                    <path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" />
                  </svg>
                  <div class="branch-main">
                    <span class="branch-name">{{ b.name }}</span>
                  </div>
                  <button
                    class="star-btn"
                    :class="{ 'is-fav': isFav(b.id) }"
                    :aria-label="isFav(b.id) ? 'เลิกปักหมุด' : 'ปักหมุด'"
                    @click.stop="toggleFav(b.id)"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      :fill="isFav(b.id) ? 'currentColor' : 'none'"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linejoin="round"
                      stroke-linecap="round"
                    >
                      <path
                        d="M12 17.3l-5.5 3.2 1.5-6.2-4.8-4.2 6.3-.5L12 4l2.5 5.6 6.3.5-4.8 4.2 1.5 6.2z"
                      />
                    </svg>
                  </button>
                  <span class="branch-arrow">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </span>
                </div>
              </div>
            </article>
          </div>
        </template>
      </div>
    </template>

    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Loading from "@/components/Loading";
import { ROLE_LABELS_TH } from "@/constants/roles";

const FAV_KEY_PREFIX = "clinic_favorites:";

export default {
  name: "ClinicViews",
  components: {
    Loading,
  },
  data() {
    return {
      isLoading: false,
      query: "",
      // Branch IDs the user has pinned. Persisted to localStorage per user.
      favorites: [],
    };
  },
  created() {
    this.init();
  },
  mounted() {
    this.loadFavorites();
    window.addEventListener("keydown", this.onShortcut);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onShortcut);
  },
  computed: {
    ...mapGetters({
      authorization: "moduleBranch/getAuthorization",
      clinicList: "moduleUser/getClinicList",
      centralClinics: "moduleUser/getCentralClinics",
      userInfo: "moduleUser/getUserInfo",
      isManager: "moduleUser/isManager",
      isDoctor: "moduleUser/isDoctor",
      isCounter: "moduleUser/isCounter",
      isAssistant: "moduleUser/isAssistant",
      isHeadCounter: "moduleUser/isHeadCounter",
      viewFile: "moduleContent/viewFile",
      isStockManager: "moduleUser/isStockManager",
      isStockStaff: "moduleUser/isStockStaff",
    }),
    // Merge branch-membership clinics (/user/clinic) with central clinics
    // (/user/central-clinics) keyed by clinic code → grouped chooser rows.
    workspaceGroups() {
      const byCode = new Map();
      for (const c of this.clinicList || []) {
        byCode.set(c.code, {
          code: c.code,
          name: c.name,
          logo: c.logo || null,
          isCentral: false,
          centralRoleKeys: [],
          branches: c.branches || [],
        });
      }
      for (const cc of this.centralClinics || []) {
        const g = byCode.get(cc.code) || {
          code: cc.code,
          name: cc.name,
          logo: null,
          isCentral: false,
          centralRoleKeys: [],
          branches: [],
        };
        g.isCentral = true;
        g.centralRoleKeys = cc.roleKeys || [];
        g.logo = g.logo || cc.logo || null;
        byCode.set(cc.code, g);
      }
      return Array.from(byCode.values());
    },
    totalWorkspaces() {
      return this.workspaceGroups.reduce(
        (n, g) => n + (g.isCentral ? 1 : 0) + (g.branches ? g.branches.length : 0),
        0
      );
    },
    // Client-side search across clinic names and branch names.
    filteredGroups() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.workspaceGroups;
      return this.workspaceGroups
        .map(g => {
          const clinicMatch = g.name.toLowerCase().includes(q);
          const branches = clinicMatch
            ? g.branches
            : g.branches.filter(b =>
                (b.name || "").toLowerCase().includes(q)
              );
          return { ...g, branches };
        })
        .filter(
          g =>
            g.name.toLowerCase().includes(q) ||
            g.branches.length > 0 ||
            (g.isCentral && "จัดการระดับคลินิก".includes(q))
        );
    },
    // Favorited branches flattened, paired with their clinic, for the pinned strip.
    pinnedBranches() {
      const out = [];
      for (const g of this.workspaceGroups) {
        for (const b of g.branches || []) {
          if (this.isFav(b.id)) out.push({ clinic: g, branch: b });
        }
      }
      return out;
    },
    userName() {
      const u = this.userInfo || {};
      const th = [u.firstNameTh, u.lastNameTh].filter(Boolean).join(" ");
      const en = [u.firstNameEn, u.lastNameEn].filter(Boolean).join(" ");
      return th || en || "";
    },
    userInitials() {
      const u = this.userInfo || {};
      const a = (u.firstNameEn || u.firstNameTh || "").trim();
      const b = (u.lastNameEn || u.lastNameTh || "").trim();
      const ini = (a[0] || "") + (b[0] || "");
      return ini ? ini.toUpperCase() : "?";
    },
    shortcutHint() {
      const isMac =
        typeof navigator !== "undefined" &&
        /Mac|iPhone|iPad/.test(navigator.userAgent || "");
      return isMac ? "⌘K" : "Ctrl K";
    },
  },
  methods: {
    ...mapActions({
      fetchBranchByUrl: "moduleBranch/fetchBranchByUrl",
      fetchClinicList: "moduleUser/fetchClinicList",
      setStateBranchInfo: "moduleUser/setStateBranchInfo",
      loadCentralClinics: "moduleUser/loadCentralClinics",
    }),
    init() {
      this.isLoading = true;
      this.fetchClinicList()
        .then(async () => {
          await this.loadCentralClinics().catch(() => {});

          // Exactly one workspace total → enter it directly (keeps the simple case simple).
          if (this.totalWorkspaces === 1) {
            const g = this.workspaceGroups[0];
            if (g.isCentral && g.branches.length === 0) {
              this.enterCentral(g);
              return;
            }
            if (!g.isCentral && g.branches.length === 1) {
              await this.selectBatch(g, g.branches[0]);
              return;
            }
          }
        })
        .catch(() => {
          this.$router.push("/logout");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // One entry per central (clinic-level) role the user holds, so each renders
    // as its own chip and the row wraps instead of overflowing the card.
    centralRoles(g) {
      const keys = g.centralRoleKeys || [];
      if (!keys.length) return [{ key: "RDM", label: "RDM" }];
      return keys.map(k => ({ key: k, label: ROLE_LABELS_TH[k] || k }));
    },
    enterCentral(group) {
      this.$router.push({
        name: "ClinicCentral",
        params: { clinicUrl: group.code },
      });
    },
    navigateBack() {
      window.history.back();
    },
    async selectBatch(clinic, branch) {
      await this.getBranchInfo(clinic.code, branch.code);
    },
    async getBranchInfo(clinicUrl, branchUrl) {
      if (clinicUrl && branchUrl) {
        this.isLoading = true;
        await this.fetchBranchByUrl({
          clinicUrl: clinicUrl,
          branchUrl: branchUrl,
        })
          .then(res => {
            if (res.status == 200) {
              this.setStateBranchInfo(res.data);
              var authId = this.authorization.authId;
              if (this.isDoctor) {
                this.$router.push("/doctor/dashboard").then(() => {});
              } else if (
                this.isManager ||
                this.isCounter ||
                this.isHeadCounter
              ) {
                this.$router.push("/queuemanage").then(() => {});
              } else if (this.isAssistant) {
                this.$router.push("/doctorsTxQ").then(() => {});
              } else if (this.isStockManager || this.isStockStaff) {
                this.$router.push("/productInventory").then(() => {});
              }
            }
          })
          .catch(err => {
            console.error(err);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    // ─── Favorites (localStorage, client-side only) ───
    favKey() {
      const uid = (this.userInfo && this.userInfo.uid) || "anon";
      return FAV_KEY_PREFIX + uid;
    },
    loadFavorites() {
      try {
        const raw = localStorage.getItem(this.favKey());
        const parsed = raw ? JSON.parse(raw) : [];
        this.favorites = Array.isArray(parsed) ? parsed.map(String) : [];
      } catch (e) {
        this.favorites = [];
      }
    },
    persistFavorites() {
      try {
        localStorage.setItem(this.favKey(), JSON.stringify(this.favorites));
      } catch (e) {
        // Storage unavailable (private mode / quota) — favorites stay in-memory only.
        console.warn("ไม่สามารถบันทึกรายการปักหมุดได้", e);
      }
    },
    isFav(id) {
      return this.favorites.includes(String(id));
    },
    toggleFav(id) {
      const key = String(id);
      const i = this.favorites.indexOf(key);
      if (i === -1) this.favorites.push(key);
      else this.favorites.splice(i, 1);
      this.persistFavorites();
    },
    // Resolve a clinic's stored logo contentId to a viewable URL (null if none).
    logoUrl(group) {
      return group && group.logo ? this.viewFile(group.logo) : null;
    },
    // Hide a broken/failed logo image so the initials fallback shows through.
    onLogoError(e) {
      if (e && e.target) e.target.style.display = "none";
    },
    // Deterministic logo-tile accent per clinic for visual variety.
    accentFor(code) {
      const variants = ["", "teal", "warm", "neutral"];
      const s = String(code || "");
      let h = 0;
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
      return variants[h % variants.length];
    },
    // Placeholder logo initials from a clinic name.
    initials(name) {
      if (!name) return "?";
      const cleaned =
        name
          .replace(/^(คลินิก(ทันตกรรม)?|บริษัท|บจก\.?|หจก\.?)\s*/u, "")
          .trim() || name;
      const parts = cleaned.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return cleaned.slice(0, 2).toUpperCase();
    },
    onShortcut(e) {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        const el = this.$refs.searchInput;
        if (el) el.focus();
      }
    },
  },
};
</script>

<style scoped>
/* ─── Professional medical theme · Variation A "Workspace Picker" ─── */
.clinic-picker {
  --bg: #f7f8fa;
  --surface: #ffffff;
  --surface-2: #f1f4f8;
  --border: #e3e8ef;
  --border-strong: #d2d9e3;
  --ink: #0f1d2e;
  --ink-2: #38465a;
  --ink-3: #6b7787;
  --ink-4: #94a0b1;
  --accent: #1f72d4;
  --accent-ink: #0b4d99;
  --accent-soft: #e8f1fc;
  --accent-soft-2: #d3e3f7;
  --teal: #0f8a86;
  --teal-soft: #e1f3f2;
  --warning: #b46708;
  --warning-soft: #fdf2dc;
  --radius: 12px;
  --shadow-xs: 0 1px 0 rgba(15, 29, 46, 0.04);
  --shadow-sm: 0 1px 2px rgba(15, 29, 46, 0.06), 0 1px 0 rgba(15, 29, 46, 0.03);
  --shadow-md: 0 6px 16px -8px rgba(15, 29, 46, 0.12),
    0 2px 4px rgba(15, 29, 46, 0.05);
  --font-sans: "Inter", "IBM Plex Sans Thai", "Noto Sans Thai", -apple-system,
    BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  --font-mono: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace;

  min-height: 100%;
  background: var(--bg);
  color: var(--ink);
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.clinic-picker *,
.clinic-picker *::before,
.clinic-picker *::after {
  box-sizing: border-box;
}

/* ── Top bar ── */
.clinic-picker .topbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}
.clinic-picker .topbar .spacer {
  flex: 1;
}
.clinic-picker .back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  border-radius: 999px;
  padding: 6px 12px 6px 10px;
  font-size: 13px;
  color: var(--ink-2);
  cursor: pointer;
  font-weight: 500;
  transition: background 0.12s;
}
.clinic-picker .back-btn:hover {
  background: var(--surface-2);
}
.clinic-picker .user-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  border-radius: 999px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  font-size: 12px;
  color: var(--ink-2);
}
.clinic-picker .user-pill .user-name {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.clinic-picker .avatar {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: linear-gradient(135deg, #b9d4f0, #1f72d4);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ── Heading + search ── */
.clinic-picker .heading {
  padding: 26px 32px 18px;
}
.clinic-picker .heading-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.clinic-picker .heading h1 {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.clinic-picker .heading p {
  margin: 0;
  color: var(--ink-3);
  font-size: 14px;
}
.clinic-picker .search-wrap {
  width: 360px;
  max-width: 100%;
}
.clinic-picker .search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: 10px;
  padding: 0 12px;
  height: 40px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.clinic-picker .search:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 4px rgba(31, 114, 212, 0.12);
}
.clinic-picker .search svg {
  color: var(--ink-3);
  flex-shrink: 0;
}
.clinic-picker .search input {
  border: 0;
  outline: none;
  background: transparent;
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: var(--ink);
  font-family: inherit;
}
.clinic-picker .search input::placeholder {
  color: var(--ink-4);
}
.clinic-picker .kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: auto;
  border-radius: 4px;
  background: #fff;
  border: 1px solid var(--border-strong);
  border-bottom-width: 2px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--ink-2);
  font-weight: 600;
  white-space: nowrap;
}

/* ── Body ── */
.clinic-picker .picker-body {
  padding: 0 32px 32px;
}

.clinic-picker .divider-label {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 12px;
  color: var(--ink-3);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.clinic-picker .divider-label::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--border);
}
.clinic-picker .pinned-section {
  margin-bottom: 20px;
}
.clinic-picker .pinned-section .divider-label {
  margin: 4px 0 12px;
}

/* ── Chips ── */
.clinic-picker .chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  color: var(--ink-2);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.clinic-picker .chip.accent {
  background: var(--accent-soft);
  border-color: var(--accent-soft-2);
  color: var(--accent-ink);
}

/* ── Cards ── */
.clinic-picker .card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-xs);
}

/* Pinned strip */
.clinic-picker .pinned-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}
.clinic-picker .pinned-card {
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: border-color 0.12s, box-shadow 0.12s, transform 0.12s;
}
.clinic-picker .pinned-card:hover {
  border-color: var(--accent);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}
.clinic-picker .pinned-text {
  flex: 1;
  min-width: 0;
}
.clinic-picker .pinned-name {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.clinic-picker .pinned-clinic {
  font-size: 11px;
  color: var(--ink-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Clinic grid (comfy density) */
.clinic-picker .clinic-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
}
.clinic-picker .clinic-card {
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.clinic-picker .clinic-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.clinic-picker .clinic-head-text {
  flex: 1;
  min-width: 0;
}
.clinic-picker .clinic-name {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--ink);
  word-break: break-word;
}
.clinic-picker .clinic-chips {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}

/* ── Logo tile ── */
.clinic-picker .logo-tile {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  border: 1px solid var(--accent-soft-2);
  font-weight: 700;
  font-size: 18px;
  letter-spacing: -0.02em;
  flex-shrink: 0;
  overflow: hidden;
}
/* Real clinic logo layered over the initials fallback; @error hides it on
   load failure so the initials show through. */
.clinic-picker .logo-tile .logo-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: var(--surface);
}
.clinic-picker .logo-tile.sm {
  width: 36px;
  height: 36px;
  font-size: 13px;
  border-radius: 9px;
}
.clinic-picker .logo-tile.lg {
  width: 96px;
  height: 96px;
  font-size: 30px;
  border-radius: 18px;
}
.clinic-picker .logo-tile.teal {
  background: var(--teal-soft);
  color: var(--teal);
  border-color: #c3e3e1;
}
.clinic-picker .logo-tile.warm {
  background: var(--warning-soft);
  color: var(--warning);
  border-color: #f5dfb0;
}
.clinic-picker .logo-tile.neutral {
  background: var(--surface-2);
  color: var(--ink-2);
  border-color: var(--border);
}

/* ── Branch rows (outlined) ── */
.clinic-picker .branch-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 0;
}
.clinic-picker .branch-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 13px 14px;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, transform 0.12s,
    box-shadow 0.12s;
  color: var(--ink);
}
.clinic-picker .branch-btn:hover,
.clinic-picker .branch-btn:focus-visible {
  border-color: var(--accent);
  background: var(--accent-soft);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
  outline: none;
}
.clinic-picker .branch-glyph {
  color: var(--ink-3);
  flex-shrink: 0;
}
.clinic-picker .branch-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.clinic-picker .branch-name {
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.clinic-picker .branch-arrow {
  display: inline-flex;
  color: var(--ink-4);
  transition: transform 0.12s, color 0.12s;
}
.clinic-picker .branch-btn:hover .branch-arrow,
.clinic-picker .branch-btn:focus-visible .branch-arrow {
  color: var(--accent);
  transform: translateX(2px);
}
.clinic-picker .branch-btn:hover .branch-glyph {
  color: var(--accent);
}

/* Management (RDM / central) row */
.clinic-picker .branch-btn.is-manage {
  background: linear-gradient(180deg, #f5f9ff, #ecf3fd);
  border-color: var(--accent-soft-2);
  color: var(--accent-ink);
}
.clinic-picker .branch-btn.is-manage .branch-glyph {
  color: var(--accent-ink);
}
.clinic-picker .branch-btn.is-manage:hover {
  background: var(--accent-soft);
}

/* ── Favorite star ── */
.clinic-picker .star-btn {
  background: transparent;
  border: 0;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  color: var(--ink-4);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.12s, color 0.12s;
}
.clinic-picker .star-btn:hover {
  background: var(--surface-2);
  color: var(--warning);
}
.clinic-picker .star-btn.is-fav {
  color: var(--warning);
}

/* ── Empty / no-workspace states ── */
.clinic-picker .empty {
  padding: 28px;
  text-align: center;
  color: var(--ink-3);
  font-size: 13px;
  background: var(--surface-2);
  border: 1px dashed var(--border-strong);
  border-radius: 12px;
}
.clinic-picker .empty strong {
  display: block;
  color: var(--ink);
  font-weight: 600;
  margin-bottom: 4px;
}
.clinic-picker .no-workspace {
  display: flex;
  justify-content: center;
  padding: 64px 32px;
}
.clinic-picker .no-workspace-card {
  max-width: 480px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 40px 32px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
}
.clinic-picker .no-workspace-card svg {
  color: var(--ink-4);
}
.clinic-picker .no-workspace-card strong {
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);
}
.clinic-picker .no-workspace-card p {
  margin: 0;
  color: var(--ink-3);
  font-size: 14px;
  line-height: 1.6;
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .clinic-picker .heading {
    padding: 20px 16px 14px;
  }
  .clinic-picker .picker-body {
    padding: 0 16px 24px;
  }
  .clinic-picker .search-wrap {
    width: 100%;
  }
  .clinic-picker .clinic-grid {
    grid-template-columns: 1fr;
  }
}
</style>
